/* global Office, console, setTimeout */

import React, { useEffect, useMemo, useState } from "react";
import { TgkParameterMetadata } from "../../../api/models/tgk-parameter-metadata";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import { selectCpmUsername } from "../../../redux/feature/settings/configurations-slice";
import {
  ComponentReference,
  CoObjectGeneralInfo,
  CoObjectItem,
  CoObjectParameters,
  DimensionDetails,
  SheetForm,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../interfaces/CoObject";
import NotificationManager from "../../../common/notification/NotificationManager";
import { descriptionLanguage, formatMessage, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import {
  addContentControlCustomXml,
  deleteXMLPart,
  retrieveDocumentProp,
} from "../../../utils/helpers/CustomXmlHelper";
import { fetchDocumentMetadata, fetchDocumentPartDetails } from "../../../api/document/DocumentsPart";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import LoadingManager from "../../../common/loading/LoadingManager";
import { TABLE_OBJECT_PARAMETERS, TGK_TEXT_OBJECT_DATASET_FIELDS } from "../../../utils/constants/notification";
import { fetchParametersMetadata } from "../../../api/object/Parameters";
import { AdvancedDocumentMetadata } from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { store } from "../../../redux/store/store";
import { ElaborationRequest } from "../../../api/object/RefreshObject";
import { deleteObjectAsync, lockObject, updateComponentInstance } from "../../../redux/feature/cpm/cpm-slice";
import {
  getUpdatedDimensions,
  getUpdatedParameters,
  mapCoObjectToRefreshElaborationItem,
} from "../../../redux/feature/cpm/map-objects";
import { createElaborationRequestAsync } from "../../../redux/feature/object/remote-elaborations-slice";
import { CustomModalProps } from "../../layout/ActionModal";
import {
  ObjectPanelPivotProps,
  ObjectPanelTabs,
  ObjectPanelValidator,
  TableUpdateStrategy,
} from "./ObjectPanelComponent";
import * as uuid from "uuid";
import { DocumentParameters } from "../../document/ParametersDocument";
import { fetchFileExist } from "../../../api/object/ExcelFile";
import { DatasetField } from "../../../api/models/dataset-field";
import { extractMandatoryDimensionsFromDatasetFields, fetchDatasetFields } from "../../../api/object/DatasetField";
import { inheritComponentData, validateComponentData } from "../../../utils/helpers/ComponentInstanceHelper";
import { CoObjectTypeEnum } from "../../../api/models";
import { moveCursorToContentControlId } from "../../../office/WordDocumentPart";
import { navigate } from "../../../redux/feature/general/navigation-slice";
import { EDIT_OBJECT } from "../../../utils/constants/navigation";
import { isAddNewPossible, isCursorInsideTable } from "../../../office/ContentControl";
import { getAdvancedTable } from "../table/CoObjectAdvancedPanelTable";
import { getReferringScenarios, isScenarioMetadata } from "./ReferringScenarios";
import { ReferringScenarios } from "../../../api/models/ReferringScenarios";
import { CPMVersions, isCpmVersionLess } from "../../../utils/helpers/ApiResolver";
import { getOriginalDimensionFromFather } from "./DimensionsHelpers";
import { fetchDimensions } from "../../../api/object/Dimensions";
import { Dimension } from "../../../api/models/dimension";

const initialStateTabs: ObjectPanelTabs[] = [
  {
    key: "0",
    name: "cof.documentproperties.generalinfo",
    isValid: false,
  },
];

const initialState: ObjectPanelState = {
  tabs: initialStateTabs,
  sourceEdited: false,
  selectedKey: 0,
  fatherDocumentParameters: undefined,
  tableUpdateType: "update",
};

export interface ObjectPanelState {
  tabs: ObjectPanelTabs[];
  sourceEdited: boolean;
  //TODO: To be renamed to "elaborate"
  selectedKey: number;
  fatherDocumentParameters: DocumentParameters[] | undefined;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
}

export const getInitialTabs = (isEdit: boolean): ObjectPanelTabs[] => [
  {
    key: "0",
    name: "cof.documentproperties.generalinfo",
    isValid: isEdit,
  },
];

const addSourceTab = (tabs: ObjectPanelTabs[], isEdit: boolean, coObject: CoObjectItem): void => {
  const isComponentInstance = coObject?.info?.componentReference !== undefined;
  tabs.push({
    key: "1",
    name: "cof.createnewobject.source",
    isValid: isEdit || isComponentInstance,
  });
};

const addParametersTab = (
  tabs: ObjectPanelTabs[],
  type: string,
  isEdit: boolean,
  coObject: CoObjectItem,
  sourceTable?: TgkTableObjectSource
): void => {
  const isComponentInstance = coObject?.info?.componentReference !== undefined;
  if (
    ((isEdit || isComponentInstance) && coObject?.source?.type === "AMOUNT_AIH") ||
    ((type === "TABLE" || type === "CHART") && sourceTable?.objectSource !== SourceTypeEnum.EXCEL) ||
    (type === "AMOUNT" && sourceTable?.objectSource === SourceTypeEnum.TAGETIK_FORM) ||
    type === "TGK_TEXT"
  ) {
    tabs.push({
      key: "2",
      name: "cof.createnewobject.parameters",
      isValid: isEdit || isComponentInstance,
    });
  }
};

const addAdvancedTab = (
  tabs: ObjectPanelTabs[],
  type: string,
  isEdit: boolean,
  coObject: CoObjectItem,
  sourceTable?: TgkTableObjectSource
): void => {
  const isComponentInstance = coObject?.info?.componentReference !== undefined;
  if (
    (type === "TABLE" || type === "CHART" || type === "AMOUNT") &&
    (coObject.advanced || !isEdit || isComponentInstance)
  ) {
    tabs.push({
      key:
        type === "TABLE" ||
        type === "CHART" ||
        (type === "AMOUNT" && sourceTable?.objectSource === SourceTypeEnum.TAGETIK_FORM) ||
        coObject?.source?.type === "AMOUNT_AIH"
          ? "3"
          : "2",
      name: "cof.createnewobject.advanced",
      isValid: isEdit || isComponentInstance,
    });
  }
};

const notifyIfMissingParameters = (coObject: CoObjectItem, mandatoryMetadata: TgkParameterMetadata[]) => {
  if (!hasAllMandatoryParameters(mandatoryMetadata, coObject.parameters)) {
    NotificationManager.addNotification({
      key: "inheritparameters.error",
      title: "",
      message: formatMessage("cof.createnewobject.inheritparameters.error"),
      type: "error",
      isTemporary: false,
    });
  }
};

const hasAllMandatoryParameters = (
  mandatoryMetadata: TgkParameterMetadata[],
  objectParameters: CoObjectParameters | DocumentParameters[]
) => {
  if (!mandatoryMetadata || mandatoryMetadata.length === 0) return true;
  if (!objectParameters) return false;

  return mandatoryMetadata
    .filter((m) => m.type === "dimension")
    .every((m) => (objectParameters as CoObjectParameters).some((p) => p.structureCode === m.dimensionCode));
};

const initTabsByType = (type: string, isEdit: boolean, coObject: CoObjectItem): ObjectPanelTabs[] => {
  const sourceTable = coObject.source as TgkTableObjectSource;
  let tabs = getInitialTabs(isEdit);

  const sourceTypes = ["DROPDOWN_LIST", "TABLE", "AMOUNT", "AMOUNT_AIH", "DIMENSION_ATTRIBUTE", "CHART", "TGK_TEXT"];
  if (sourceTypes.includes(type)) {
    addSourceTab(tabs, isEdit, coObject);
  }

  addParametersTab(tabs, type, isEdit, coObject, sourceTable);
  addAdvancedTab(tabs, type, isEdit, coObject, sourceTable);

  return tabs;
};

export interface AvailableParameters {
  mandatoryParametersMetadata: TgkParameterMetadata[];
  optionalParametersMetadata: TgkParameterMetadata[];
}
export interface AvailableDimensions {
  mandatoryDimensionsMetadata: Dimension[];
  optionalDimensionsMetadata: Dimension[];
}

const useObjectPanelComponent = (props: ObjectPanelPivotProps) => {
  const [state, setState] = React.useState<ObjectPanelState>(initialState);
  const [originalSheetArea, setOriginalSheetArea] = React.useState<SheetForm>({ uid: "", desc: "", code: "" });
  const [availableParametersMetadata, setParametersMetadata] = React.useState<AvailableParameters>({
    mandatoryParametersMetadata: [],
    optionalParametersMetadata: [],
  });
  const [availableDimensionsMetadata, setDimensionsMetadata] = React.useState<AvailableDimensions>({
    mandatoryDimensionsMetadata: [],
    optionalDimensionsMetadata: [],
  });
  const [datasetFields, setDatasetFields] = React.useState<DatasetField[]>([]);

  const { isEdit, isComponentInstance, coObject, onSaveBtn, validator, isLocked } = props;
  const [pasteMode, setPasteMode] = React.useState<boolean>(false);
  const { selectedKey, tabs } = state;
  const selectedTab = useMemo(() => tabs.find((t) => String(selectedKey) == t.key), [tabs, selectedKey]);
  const username = useAppSelector(selectCpmUsername);
  const [isFileExcelExisting, setIsFileExcelExisting] = useState<boolean>(true);
  const [fileExcelName, setFileExcelName] = useState<string>("");
  const [isOldSheetOrAreaSelected, setIsOldSheetOrAreaSelected] = useState<boolean>(false);
  const [isLoadingSheetAndArea, setIsLoadingSheetAndArea] = useState<boolean>(false);
  const [isSheetChartAndAreaToHide, setIsSheetChartAndAreaToHide] = useState(false);
  useEffect(() => {
    if (!coObject) return; // Add this check to prevent errors
    setState((s) => ({
      ...s,
      tabs: props.coObject?.info ? initTabsByType(props.coObject.info.objectType, isEdit, coObject) : initialStateTabs,
      sourceEdited: false,
      selectedKey: 0,
      tableUpdateType: "update",
    }));
    checkFileExcelExists();
  }, [coObject.contentControlId]);

  function checkFileExcelExists() {
    const tableSource = coObject.source as TgkTableObjectSource;
    if (isEdit && tableSource?.objectSource == SourceTypeEnum.EXCEL) {
      fetchFileExist(coObject.documentId, tableSource.form.code).then((resp) => {
        if (resp?.path == "") {
          setIsFileExcelExisting(false);
        } else {
          tableSource.form.oidPath = resp?.path?.split(",");
          setFileExcelName(resp?.fileName);
          setIsFileExcelExisting(true);
        }
        setOriginalSheetArea(tableSource.sheet);
      });
    }
    return isFileExcelExisting;
  }

  const [inheritableParametersInThisDb, setInheritableParametersInThisDb] = useState<DimensionDetails[]>();

  useEffect(() => {
    (async () => {
      const dbId = (coObject?.source as TgkTableObjectSource)?.dbApplication?.id;
      if (!dbId || isComponentInstance || !state.fatherDocumentParameters) {
        setInheritableParametersInThisDb(null);
        return;
      }
      const dbParams = state.fatherDocumentParameters.find((p) => p.dbId === dbId)?.dimensions ?? [];
      const newInheritableParametersInThisDb: DimensionDetails[] = [];
      for (const p of dbParams) {
        if (!isScenarioMetadata(p) || p.values[0]?.code) {
          newInheritableParametersInThisDb.push(p);
          continue;
        }
        // IDS-1100 retrocompatibility case
        const thisItem = await getOriginalDimensionFromFather(p, dbId);
        if (!thisItem) {
          newInheritableParametersInThisDb.push(p);
          continue;
        }
        console.log(`retrieved scenario value code ${thisItem.code}`);
        newInheritableParametersInThisDb.push({ ...p, values: p.values.map((v) => ({ ...v, code: thisItem.code })) });
      }
      setInheritableParametersInThisDb(newInheritableParametersInThisDb);
    })();
  }, [
    (coObject?.source as TgkTableObjectSource)?.dbApplication?.id,
    state.fatherDocumentParameters,
    isComponentInstance,
  ]);

  // keyed scenarioCode or undefined for dimensions
  const [referringScenarios, setReferringScenarios] = useState<ReferringScenarios | null>();

  useEffect(() => {
    (async () => {
      const dbId = (coObject?.source as TgkTableObjectSource)?.dbApplication?.id;

      if (isComponentInstance) return;

      let newReferringScenario: ReferringScenarios;
      if (!isCpmVersionLess(CPMVersions.CPM_VERSION32_0)) {
        newReferringScenario = await getReferringScenarios(inheritableParametersInThisDb, dbId);
        setReferringScenarios(newReferringScenario);
      }
      if ((isEdit || isComponentInstance) && state.fatherDocumentParameters && dbId) {
        let isChanged = false;

        let newCoObject = {
          ...coObject,
        };
        if (coObject.parameters) {
          const { newParameters, paramsChanged } = getUpdatedParameters(
            coObject.parameters as CoObjectParameters,
            inheritableParametersInThisDb,
            newReferringScenario
          );
          newCoObject.parameters = newParameters;
          isChanged = isChanged || paramsChanged;
        }
        const sources = coObject.source as TgkDimensionAttributeSource;
        if (sources?.dimensions) {
          const { newDimensions, dimChanged } = getUpdatedDimensions(
            sources.dimensions,
            inheritableParametersInThisDb,
            newReferringScenario
          );
          (newCoObject.source as TgkDimensionAttributeSource).dimensions = newDimensions;
          isChanged = isChanged || dimChanged;
        }

        props.saveCoObject(newCoObject, { ...validator, modifiedSource: isChanged, modifiedFields: isChanged });
      }
    })();
  }, [inheritableParametersInThisDb]);

  useEffect(() => {
    if (!isComponentInstance) return;
    const activeRefs = [
      ...((coObject?.parameters as CoObjectParameters) ?? []),
      ...((coObject?.source as TgkAmountObjectSource)?.dimensions ?? []),
    ]
      .map((d) => d.values?.[0]?.ref)
      .filter((s) => s);
    setReferringScenarios({
      code: "",
      active: true,
      referenceScenarios: activeRefs.map((r) => ({ code: "", description: "", reference: r })),
    }); //this is made to show ref scenario input even if no fetch was made
    props.saveCoObject(coObject, validator);
  }, [isComponentInstance, coObject.source, coObject.parameters]);

  useEffect(() => {
    if (isComponentInstance) return;
    const getInheritableParameters = async () => {
      const documentId = await retrieveDocumentProp("Document-ID");
      const fatherMetadata = await fetchDocumentMetadata(documentId);
      setState((s) => ({ ...s, fatherDocumentParameters: fatherMetadata.parametersMetadata ?? [] }));
    };
    getInheritableParameters();
  }, []);

  async function getParametersMetadata(objectSource: TgkTableObjectSource): Promise<AvailableParameters> {
    if (objectSource.objectSource === SourceTypeEnum.EXCEL) return;
    if (!objectSource?.form?.code) {
      return {
        mandatoryParametersMetadata: [],
        optionalParametersMetadata: [],
      };
    }
    LoadingManager.create(TABLE_OBJECT_PARAMETERS);
    try {
      const parametersMetadata = await fetchParametersMetadata(objectSource.dbApplication.id, objectSource.form.code);
      const mandatoryParametersMetadata = parametersMetadata.filter((dimension) => dimension.mandatory);
      const userSelectedParametersMetadata = parametersMetadata.filter((dimension) => !dimension.mandatory);
      const inheritableParametersMap = parametersMetadata.reduce<Map<string, TgkParameterMetadata>>((acc, p) => {
        if (acc.has(p.dimensionCode)) acc.set(p.dimensionCode, null);
        else acc.set(p.dimensionCode, p);
        return acc;
      }, new Map());

      if (!isEdit && !isComponentInstance && !pasteMode) {
        const thisDimensionsInheritableParameters = (inheritableParametersInThisDb || [])
          .map((d) => {
            const parameter = inheritableParametersMap.get(d.structureCode);
            if (parameter) return { ...d, code: parameter.code, inherited: true };
            return null;
          })
          .filter(Boolean);
        const newCoObject = {
          ...coObject,
          parameters: thisDimensionsInheritableParameters,
        };
        props.saveCoObject(newCoObject, validator);
      }

      if (isEdit) {
        notifyIfMissingParameters(coObject, mandatoryParametersMetadata);
      }
      LoadingManager.remove(TABLE_OBJECT_PARAMETERS);

      return {
        mandatoryParametersMetadata: mandatoryParametersMetadata,
        optionalParametersMetadata: userSelectedParametersMetadata,
      };
    } catch {
      LoadingManager.remove(TABLE_OBJECT_PARAMETERS);
    }
  }

  useEffect(() => {
    if (!pasteMode)
      getParametersMetadata(props.coObject.source as TgkTableObjectSource).then((meta) => {
        if (meta) setParametersMetadata(meta);
      });

    return () => LoadingManager.remove(TABLE_OBJECT_PARAMETERS);
  }, [(props.coObject.source as TgkTableObjectSource)?.form, pasteMode]);

  useEffect(() => {
    const objSource = props.coObject.source as TgkAmountObjectSource;
    if (objSource?.dimensions && objSource.dbApplication?.id)
      fetchDimensions(objSource.dbApplication.id).then((dimensions) => {
        const mandatoryDimensionsMetadata = dimensions.filter((dimension) => dimension.mandatory);
        const optionalDimensionsMetadata = dimensions.filter((dimension) => !dimension.mandatory);
        setDimensionsMetadata({ mandatoryDimensionsMetadata, optionalDimensionsMetadata });
      });
  }, [(props.coObject.source as TgkAmountObjectSource)?.dbApplication]);

  async function getDatasetFields() {
    const objectSource = props.coObject.source as TgkTextSource | TgkAmountObjectSource;
    if (!objectSource?.dataset?.code) {
      setDatasetFields([]);
      return;
    }
    LoadingManager.create(TGK_TEXT_OBJECT_DATASET_FIELDS);
    const datasetFields = await fetchDatasetFields(
      objectSource.dbApplication.id.toString(),
      objectSource.analyticalWorkspace.code.toString(),
      objectSource.dataset.code.toString(),
      objectSource.dataset.partitionType.toString(),
      descriptionLanguage()
    );
    setDatasetFields(datasetFields);
    LoadingManager.remove(TGK_TEXT_OBJECT_DATASET_FIELDS);
  }

  useEffect(() => {
    if (props.coObject?.info?.objectType === "TGK_TEXT" || props.coObject?.source?.type === "AMOUNT_AIH") {
      getDatasetFields();
    }
    return () => LoadingManager.remove(TGK_TEXT_OBJECT_DATASET_FIELDS);
  }, [(props.coObject.source as TgkTextSource | TgkAmountObjectSource)?.dataset]);

  function getBreadcrumbTitle() {
    if (selectedTab) return `${formatMessage(selectedTab.name)} (${selectedKey + 1}/${tabs ? tabs.length : 1})`;
    return "";
  }

  function onClickPivot(selected) {
    if (isEdit || isComponentInstance || Number(selected.props.itemKey) < selectedKey) {
      setState({ ...state, selectedKey: Number(selected.props.itemKey), tableUpdateType: "update" });
    }
  }

  function validate(
    optionalTabValidation: ObjectPanelTabs | undefined,
    modifiedFields: boolean,
    modifiedSource: boolean,
    newCoObject: CoObjectItem
  ) {
    const isComponentInstance = newCoObject.info.componentReference !== undefined;
    let isAllValid = true;
    let tabsNew: ObjectPanelTabs[];
    if (isComponentInstance) {
      tabsNew = initTabsByType(newCoObject.info.objectType, false, newCoObject);
      return { tabs: tabsNew, validator: validateComponentData(newCoObject, modifiedFields, modifiedSource) };
    } else {
      tabsNew =
        newCoObject.info && newCoObject.info.objectType != coObject.info?.objectType
          ? initTabsByType(newCoObject.info.objectType, false, newCoObject)
          : state.tabs;
      tabsNew.forEach((t) => {
        if (t.key == optionalTabValidation?.key) {
          t.isValid = optionalTabValidation.isValid;
        }
        if (!t.isValid) isAllValid = false;
      });

      const newValidator: ObjectPanelValidator = {
        isAllValid: isAllValid,
        modifiedFields: modifiedFields || validator.modifiedFields,
        modifiedSource: modifiedSource || state.sourceEdited,
      };

      return { tabs: tabsNew, validator: newValidator };
    }
  }

  async function updateWithComponentData(
    objectType: CoObjectTypeEnum,
    componentReference: ComponentReference
  ): Promise<void> {
    const inheritedCoObject: CoObjectItem = await inheritComponentData({
      ...coObject,
      info: {
        ...coObject.info,
        objectType: objectType,
        componentReference: componentReference,
      },
    });
    props.saveCoObject(inheritedCoObject, validateComponentData(inheritedCoObject, false, true));
    setState((s) => ({
      ...s,
      tabs: initTabsByType(inheritedCoObject.source.type, false, inheritedCoObject),
    }));
  }

  function updateObjectInfo(
    objInfo: CoObjectGeneralInfo,
    optionalTabValidation: ObjectPanelTabs | undefined,
    modifiedFields: boolean,
    modifiedType?: boolean,
    modifiedSources?: boolean
  ) {
    const newCoObject = {
      ...coObject,
      info: objInfo,
      source: modifiedType ? null : coObject.source,
      advanced: modifiedType ? null : coObject.advanced,
    };

    const validateObject = validate(optionalTabValidation, modifiedFields, modifiedSources, newCoObject);

    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({
      ...s,
      tabs: validateObject.tabs,
      tableUpdateType: "update",
    }));
  }

  const manageTabs = (objSource) => {
    switch (objSource?.type) {
      case "TABLE":
      case "CHART":
        if (objSource.objectSource === SourceTypeEnum.EXCEL && tabs.length !== 3) {
          updateTabs([{ key: "2", name: "cof.createnewobject.advanced", isValid: isEdit }]);
        } else if (objSource.objectSource !== SourceTypeEnum.EXCEL && tabs.length === 3) {
          updateTabs([
            { key: "2", name: "cof.createnewobject.parameters", isValid: isEdit },
            { key: "3", name: "cof.createnewobject.advanced", isValid: isEdit },
          ]);
        }
        break;
      case "AMOUNT":
      case "AMOUNT_AIH":
        if (
          (objSource.objectSource === SourceTypeEnum.TAGETIK_FORM && tabs.length !== 4) ||
          objSource.type === "AMOUNT_AIH"
        ) {
          updateTabs([
            { key: "2", name: "cof.createnewobject.parameters", isValid: isEdit },
            { key: "3", name: "cof.createnewobject.advanced", isValid: isEdit },
          ]);
        } else if (objSource.objectSource !== SourceTypeEnum.TAGETIK_FORM && tabs.length === 4) {
          updateTabs([{ key: "2", name: "cof.createnewobject.advanced", isValid: isEdit }]);
        }
        break;
    }
  };

  const updateTabs = (tabConfig) => {
    tabs.splice(2);
    tabConfig.forEach((config) => tabs.push(config));
  };

  function updateFileExcelName(newFileNameExcelName: string) {
    setFileExcelName(newFileNameExcelName);
  }

  function updateObjectSource(objSource, tabValidator: ObjectPanelTabs, modifiedFields: boolean) {
    console.log({ objSource });
    manageTabs(objSource);
    let newSource;
    const newCoObject = { ...coObject };
    switch (objSource?.type) {
      case "TABLE":
      case "CHART":
        newSource = {
          objectSource: objSource.objectSource,
          dbApplication: objSource.dbApplication,
          form: objSource.form,
          sheet: objSource.sheet,
          area: objSource.area,
          chart: objSource.chart,
          oidPath: objSource.path,
          type: objSource.type,
          path: objSource.path,
          elabResultType: objSource.elabResultType,
          inheritFormatImage: objSource.inheritFormatImage,
          imageExtension: objSource.imageExtension,
          imageResolution: objSource.imageResolution,
        } as TgkTableObjectSource;

        if ((coObject?.source as TgkTableObjectSource)?.form?.code !== newSource?.form?.code)
          newCoObject.parameters = [];
        if (modifiedFields || coObject.source?.elabResultType === "IMAGE_STRINGBASE64") {
          newCoObject.info.overrideData = false;
        }
        if (modifiedFields && coObject.source?.elabResultType !== "IMAGE_STRINGBASE64") {
          newCoObject.info.overrideStyle = false;
        }
        break;
      case "AMOUNT":
      case "AMOUNT_AIH":
        console.log("BEGIN MAP", { objSource });
        newSource = {
          type: objSource.type,
          form: objSource.form,
          sheet: objSource.sheet,
          area: objSource.area,
          cell: objSource.cell,
          objectSource: objSource.objectSource,
          dbApplication: objSource.dbApplication,
          dimensions: objSource.dimensions,
          analyticalWorkspace: objSource.analyticalWorkspace,
          dataset: objSource.dataset,
          amount: objSource.amount,
          process: objSource.process,
        } as TgkAmountObjectSource;

        console.log({ coObject, newSource });
        break;
      case "DROPDOWN_LIST":
        newSource = {
          type: "DROPDOWN_LIST",
          dropDownList: objSource.dropDownList,
        } as TgkDropDownSource;
        break;
      case "DIMENSION_ATTRIBUTE":
        newSource = {
          type: "DIMENSION_ATTRIBUTE",
          dbApplication: objSource.dbApplication,
          dimensions: objSource.dimensions,
          locale: objSource.locale,
        } as TgkDimensionAttributeSource;
        break;
      case "TGK_TEXT":
        newSource = {
          type: "TGK_TEXT",
          dbApplication: objSource.dbApplication,
          analyticalWorkspace: objSource.analyticalWorkspace,
          dataset: objSource.dataset,
          richText: objSource.richText,
          process: objSource.process,
          dimensions: objSource.dimensions,
        } as TgkTextSource;
        break;
      default:
        newSource = {};
        break;
    }

    newCoObject.source = newSource;
    setState((s) => {
      const validateObject = validate(tabValidator, modifiedFields, modifiedFields || s.sourceEdited, newCoObject);
      props.saveCoObject(newCoObject, validateObject.validator);
      return { ...s, tabs: validateObject.tabs, sourceEdited: modifiedFields || s.sourceEdited };
    });
  }

  function updateObjectAdvanced(
    objAdvanced: AdvancedDocumentMetadata,
    tabValidator: ObjectPanelTabs,
    modifiedFields: boolean
  ) {
    const newCoObject = {
      ...coObject,
      advanced: objAdvanced,
    };
    const validateObject = validate(tabValidator, modifiedFields, true, newCoObject);

    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({
      ...s,
      tabs: validateObject.tabs,
    }));
  }

  function updateObjectParameters(
    objParameters: CoObjectParameters,
    tabValidator: ObjectPanelTabs,
    modifiedFields: boolean
  ) {
    const newCoObject = {
      ...coObject,
      parameters: objParameters,
    };
    const validateObject = validate(tabValidator, modifiedFields, true, newCoObject);

    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({
      ...s,
      tabs: validateObject.tabs,
    }));
  }

  async function updateObjectSourceAndParametersFromSmartObject(smartObject: CoObjectItem) {
    // only for table/chart
    const newCoObject = { ...coObject };

    if (
      ((smartObject.source as any).chart?.name?.length && coObject.info.objectType === "TABLE") ||
      (!(smartObject.source as any).chart?.name?.length && coObject.info.objectType === "CHART")
    ) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.pasteobject.message.error.cannotpaste.wrongtype"),
        type: "error",
      });
      return;
    }

    newCoObject.source = smartObject.source;
    newCoObject.parameters = (smartObject.parameters as CoObjectParameters).filter(
      (parameter: { values: string | any[] }): parameter is DimensionDetails => parameter.values.length > 0
    );

    const newDocPart = await fetchDocumentPartDetails(coObject.documentId);

    newCoObject.advanced = await getAdvancedTable(newDocPart, isEdit, newCoObject.advanced);

    const parametersMetadata = await getParametersMetadata(newCoObject.source as TgkTableObjectSource);
    if (parametersMetadata) setParametersMetadata(parametersMetadata);

    const tabsValid = state.tabs.map((value, index) => ({
      ...value,
      isValid: index !== 2 || checkParametersValidity(newCoObject, parametersMetadata ?? availableParametersMetadata),
    }));

    const validator: ObjectPanelValidator = {
      isAllValid: tabsValid.reduce((acc, tab) => tab.isValid && acc, true),
      modifiedFields: true,
      modifiedSource: true,
    };

    setPasteMode(true);
    setState((s) => ({
      ...s,
      sourceEdited: true,
      tabs: tabsValid,
    }));
    props.saveCoObject(newCoObject, validator);
  }

  function onNavigate(direction: "NEXT" | "PREV") {
    if (direction === "NEXT" && selectedKey < tabs.length) {
      setState({ ...state, selectedKey: selectedKey + 1, tableUpdateType: "update" });
    } else if (direction === "PREV" && selectedKey > 0) {
      setState({ ...state, selectedKey: selectedKey - 1, tableUpdateType: "update" });
    }
  }

  function goToTabIndex(index: number) {
    setState({ ...state, selectedKey: index, tableUpdateType: "update" });
  }

  function checkNextBtnVisible() {
    return !isEdit && selectedKey < (tabs != null ? tabs.length : 1) - 1 && !isComponentInstance;
  }

  function checkNextBtnEnabled() {
    return !isEdit && tabs[selectedKey]?.isValid && tabs && selectedKey < tabs.length - 1;
  }

  function checkInsertBtnVisible() {
    return !isEdit && tabs && (selectedKey == tabs.length - 1 || isComponentInstance || pasteMode === true);
  }

  function checkInsertBtnEnabled() {
    return (
      !isEdit &&
      tabs &&
      (selectedKey == tabs.length - 1 || isComponentInstance || pasteMode) &&
      validator.isAllValid &&
      validator.modifiedFields &&
      checkParametersValidity(coObject, availableParametersMetadata)
    );
  }

  function checkSaveBtnVisible() {
    return isEdit;
  }

  const checkMandatoryDimensions = (coObject: CoObjectItem, availableDimensionsMetadata: AvailableDimensions) => {
    const objectDimensions = (coObject?.source as TgkTextSource)?.dimensions;
    if (!objectDimensions) return true;

    if (coObject.info.objectType === "DIMENSION_ATTRIBUTE") {
      return objectDimensions.length === 1 && objectDimensions[0].values?.length === 1;
    }

    let mandatoryDimensions: Dimension[] = availableDimensionsMetadata.mandatoryDimensionsMetadata;

    if (coObject.info.objectType === "TGK_TEXT" || props.coObject?.source?.type === "AMOUNT_AIH") {
      mandatoryDimensions = datasetFields ? extractMandatoryDimensionsFromDatasetFields(datasetFields) : [];
    }

    if (!mandatoryDimensions) {
      return true;
    }

    return mandatoryDimensions.every((mandatoryDimensionRoot) =>
      objectDimensions?.some((d) => d.dimensionOid === mandatoryDimensionRoot.oid && d.values?.length)
    );
  };

  const checkParametersValidity = (coObject: CoObjectItem, availableParametersMetadata: AvailableParameters) => {
    if (coObject?.info?.componentReference != undefined) return true;
    if (coObject?.info && coObjectIsWithoutParameters(coObject)) return true;
    if (
      !availableParametersMetadata.mandatoryParametersMetadata ||
      !availableParametersMetadata.optionalParametersMetadata
    )
      return false;
    const objectParameters = coObject.parameters as CoObjectParameters;
    for (const mandatoryParameterRoot of availableParametersMetadata.mandatoryParametersMetadata) {
      if (mandatoryParameterRoot.type === "dimension") {
        const thisParameterValue = objectParameters.find(
          (d) => d.structureCode === mandatoryParameterRoot.dimensionCode && d.code === mandatoryParameterRoot.code
        );
        if (!thisParameterValue?.values?.toString()?.length) return false;
      } else {
        const thisParameterValue = objectParameters.find((d) => d.dimensionOid === mandatoryParameterRoot.oid);
        if (!thisParameterValue?.values[0]?.desc?.toString()?.length) return false;
      }
    }
    return true;
  };

  const coObjectIsWithoutParameters = (coObject) => {
    //check if objects parameter need to be checked
    return (
      (coObject.info.objectType != "TABLE" ||
        (coObject.info.objectType == "TABLE" && coObject.source.objectSource == SourceTypeEnum.EXCEL)) &&
      coObject.info.objectType != "CHART"
    );
  };

  function checkSaveBtnEnabled() {
    return (
      isEdit &&
      validator.modifiedFields &&
      validator.isAllValid &&
      checkParametersValidity(coObject, availableParametersMetadata) &&
      checkMandatoryDimensions(coObject, availableDimensionsMetadata) &&
      !(coObject?.info && coObject.info.objectType == "DOCUMENT_PART_INCLUDE") &&
      !Office.context.requirements.isSetSupported("WordApiOnline", "1.1")
    );
  }

  function checkUpdateBtnEnabled() {
    return (
      isEdit &&
      !validator.modifiedFields &&
      validator.isAllValid &&
      !Office.context.requirements.isSetSupported("WordApiOnline", "1.1")
    );
  }

  function checkUpdateBtnVisible() {
    return (
      isEdit &&
      coObject.info.objectType !== "CHECKBOX" &&
      coObject.info.objectType !== "DROPDOWN_LIST" &&
      coObject.info.objectType !== "FREE_TEXT"
    );
  }

  function checkDeleteBtnEnabled() {
    return (
      !(coObject?.info && coObject.info.objectType == "DOCUMENT_PART_INCLUDE") &&
      !Office.context.requirements.isSetSupported("WordApiOnline", "1.1")
    );
  }

  async function saveAndNavigate() {
    if (!isEdit) {
      const isInsideTable = await isCursorInsideTable();
      if (
        (coObject.info.objectType === CoObjectTypeEnum.TABLE || coObject.info.objectType === CoObjectTypeEnum.CHART) &&
        isInsideTable
      ) {
        NotificationManager.addNotification({
          title: "",
          message: formatMessage("cof.createnewobject.save.message.error.innerobject.notpermitted"),
          type: "error",
        });
        return;
      }
      isAddNewPossible().then((res) => {
        if (res) {
          onSaveBtn(state.tableUpdateType);
          setState({ ...state, selectedKey: 0, sourceEdited: false, tableUpdateType: "update" });
          return;
        }
        NotificationManager.addNotification({
          title: "",
          message: formatMessage("cof.createnewobject.save.message.error.innerobject.notpermitted"),
          type: "error",
        });
      });
    } else {
      onSaveBtn(state.tableUpdateType);
      setState({ ...state, selectedKey: 0, sourceEdited: false, tableUpdateType: "update" });
      setIsOldSheetOrAreaSelected(false);
    }
  }

  function updateLockObjectInfo(isLocked: boolean) {
    const newCoObject = {
      ...coObject,
      isLocked: isLocked,
    };
    const validateObject = validate(undefined, false, false, newCoObject);
    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({ ...s, tabs: validateObject.tabs, tableUpdateType: "update" }));
  }

  const dispatch = useAppDispatch();

  async function onUnlockObject() {
    LoadingManager.create("lock");
    await lockObject(coObject, false);
    updateLockObjectInfo(false);
    await moveCursorToContentControlId(Number(coObject.contentControlId));
    setTimeout(() => {
      dispatch(navigate(EDIT_OBJECT));
      LoadingManager.remove("lock");
    }, 1000);
  }

  async function refreshClickHandler(tableUpdateType: TableUpdateStrategy["typeUpdate"]) {
    const clientID = store.getState().configurationState.clientId;
    const correlationID = uuid.v4();

    try {
      if (coObject.info.componentReference != undefined) {
        // component instance case
        await updateComponentInstance(coObject, username);
      } else {
        // normal object case
        let elaboration: ElaborationRequest = {
          correlationId: correlationID,
          clientId: clientID,
          documentId: `${coObject.documentId}`,
          userId: username,
          tgkElaborationItemList: [],
        };

        const rootDocument = await fetchDocumentPartDetails(coObject.documentId);

        const item = await mapCoObjectToRefreshElaborationItem(
          correlationID,
          rootDocument,
          coObject,
          undefined,
          tableUpdateType,
          fileExcelName
        );
        elaboration.tgkElaborationItemList.push(item);

        if (elaboration.tgkElaborationItemList.length) {
          //if no elaboration present, if the item generated an error while updating the inherited formatting
          const elaborationResponse = await createElaborationRequestAsync(elaboration);
          // eslint-disable-next-line no-undef
          console.log("elaborationResponse: ", elaborationResponse);
          const elaborationResponseItem = elaborationResponse.elaborationResponseItemList.pop();
          if (elaborationResponseItem.targetCoObjectVersionId) {
            coObject.id = elaborationResponseItem.targetCoObjectVersionId;
            deleteXMLPart(coObject.contentControlId);
            addContentControlCustomXml(coObject.contentControlId, coObject);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const [deleted, setDeleted] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<CustomModalProps | undefined>(undefined);

  const getHeaderTitle = () => {
    if (deleted)
      return formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: coObject.info.objectName,
      });
    if (coObject.id)
      return formatMessageWithParams("cof.createnewobject.modal.edit", {
        coObjectName: coObject.info.objectName,
      });
    return selectedKey > 0 && coObject.info?.objectName
      ? formatMessage("cof.createnewobject.modal.new") + " - " + coObject.info.objectName
      : formatMessage("cof.createnewobject.modal.new");
  };

  const setDeletionConfirm = () => {
    setModalProps({
      modalTitle: formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: coObject.info.objectName,
      }),
      messageTitle: formatMessageWithParams("cof.createnewobject.modal.delete.message", {
        coObjectName: coObject.info.objectName,
      }),
      message: null,
      type: "delete",
      onHide: () => {
        return;
      },
      actions: [
        {
          id: "cancel_button",
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => {
            setModalProps(undefined);
          },
          variant: "blue",
        },
        {
          id: "confirm_button",
          title: formatMessage("cof.base.confirmdelete"),
          onClick: () => {
            deleteObjectAsync({ coObject })
              .then(() => {
                setDeleted(true);
                setModalProps(undefined);
              })
              .catch(() => {
                setDeleted(undefined);
                setModalProps(undefined);
              });
          },
          variant: "red_filled",
        },
      ],
    });
  };

  const setTableUpdateType = (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => {
    setState((s) => ({ ...s, tableUpdateType }));
  };

  return {
    tableUpdateType: state.tableUpdateType,
    getHeaderTitle,
    modalProps,
    deleted,
    isEdit,
    isComponentInstance,
    updateWithComponentData,
    selectedKey,
    onClickPivot,
    tabs,
    getBreadcrumbTitle,
    coObject,
    updateObjectInfo,
    updateObjectSource,
    updateObjectAdvanced,
    updateObjectParameters,
    updateObjectSourceAndParametersFromSmartObject,
    inheritableParametersInThisDb,
    availableParametersMetadata,
    datasetFields,
    saveAndNavigate,
    onUnlockObject,
    updateLockObjectInfo,
    onNavigate,
    checkInsertBtnEnabled,
    checkNextBtnVisible,
    checkUpdateBtnEnabled,
    checkUpdateBtnVisible,
    setDeletionConfirm,
    refreshClickHandler,
    checkInsertBtnVisible,
    checkNextBtnEnabled,
    checkSaveBtnEnabled,
    checkSaveBtnVisible,
    checkDeleteBtnEnabled,
    isLocked,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    setTableUpdateType,
    goToTabIndex,
    originalSheetArea,
    setOriginalSheetArea,
    isOldSheetOrAreaSelected,
    setIsOldSheetOrAreaSelected,
    isLoadingSheetAndArea,
    setIsLoadingSheetAndArea,
    setIsSheetChartAndAreaToHide,
    isSheetChartAndAreaToHide,
    referringScenarios,
    availableDimensionsMetadata,
  };
};

export default useObjectPanelComponent;
